/** Commercials tab for the Customer Workspace (Demo Mode only). Editable commercial model,
 * contract value, pricing basis, billing rules, rebates, margins and approval status, structured
 * as traditional form fields (bound to the parent workspace's edit draft - see `../index.tsx`).
 * Commercial assumptions are repeatable free-text lines, so they use the shared
 * `EditableStringList` rather than a single flat paragraph. Rendered inside the single Section
 * the parent workspace places directly below its Tabs. */

import { Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { CustomerRecord } from '../../../../mocks/data/customerManagementData';
import EditableStringList from '../../components/EditableStringList';

interface CommercialsTabProps {
  customer: CustomerRecord;
  onChange: (updater: (prev: CustomerRecord) => CustomerRecord) => void;
}

const APPROVAL_STATUSES: CustomerRecord['commercial']['approvalStatus'][] = ['Approved', 'PendingApproval', 'InReview'];

const CommercialsTab = ({ customer, onChange }: CommercialsTabProps) => {
  const { t } = useTranslation('common');
  const { commercial } = customer;

  const updateCommercial = (patch: Partial<CustomerRecord['commercial']>) => {
    onChange((prev) => ({ ...prev, commercial: { ...prev.commercial, ...patch } }));
  };

  return (
    <Section style={{ p: 5 }}>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('commercialsModelTitle', 'Commercial model')}</Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}>
              <TextField
                label={t('commercialsModel', 'Model')}
                value={commercial.model}
                onChange={(e) => updateCommercial({ model: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('commercialsContractValue', 'Contract value')}
                value={commercial.contractValue}
                onChange={(e) => updateCommercial({ contractValue: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('commercialsPricingBasis', 'Pricing basis')}
                value={commercial.pricingBasis}
                onChange={(e) => updateCommercial({ pricingBasis: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('commercialsManagementFee', 'Management fee')}
                value={commercial.managementFee}
                onChange={(e) => updateCommercial({ managementFee: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                label={t('commercialsRebate', 'Rebate')}
                value={commercial.rebate}
                onChange={(e) => updateCommercial({ rebate: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('commercialsMargin', 'Margin')}
                value={commercial.margin}
                onChange={(e) => updateCommercial({ margin: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('commercialsBillingTitle', 'Billing & approval')}</Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}>
              <TextField
                label={t('commercialsBillingRules', 'Billing rules')}
                value={commercial.billingRules}
                onChange={(e) => updateCommercial({ billingRules: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('commercialsPaymentTerms', 'Payment terms')}
                value={commercial.paymentTerms}
                onChange={(e) => updateCommercial({ paymentTerms: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                select
                label={t('commercialsApprovalStatus', 'Approval status')}
                value={commercial.approvalStatus}
                onChange={(e) => updateCommercial({ approvalStatus: e.target.value as CustomerRecord['commercial']['approvalStatus'] })}
                fullWidth
                size="small"
              >
                {APPROVAL_STATUSES.map((status) => (
                  <MenuItem key={status}
                    value={status}>{status}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <EditableStringList
            label={t('commercialsAssumptions', 'Commercial assumptions')}
            items={commercial.assumptions}
            onChange={(assumptions) => updateCommercial({ assumptions })}
            addLabel={t('commercialsAddAssumption', 'Add assumption')}
            placeholder={t('commercialsAssumptionPlaceholder', 'e.g. 420 covers/day average')}
          />
        </Grid>
      </Grid>
    </Section>
  );
};

export default CommercialsTab;
